import {
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    inject,
    viewChild,
} from '@angular/core';
import {
    HeightInAnimation,
    HeightOutAnimation,
    OpacityInAnimation,
    OpacityOutAnimation,
    WidthInAnimation,
    WidthOutAnimation,
    PrologueComponent,
    StickyDirective,
    FloatingToolbarComponent,
    FloatingToolbarButtonsComponent,
    FloatingToolbarSeparatorComponent,
    LoadingSlateComponent,
    ExpandableComponent,
    PagingComponent,
    HtmlSanitizePipe,
    UrlTrustPipe,
} from '@exl-ng/mulo-common';
import { MediaService, SvgViewboxDirective } from '@exl-ng/mulo-core';
import { PaginationInstance, NgxPaginationModule } from 'ngx-pagination';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    MatDialog,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
} from '@angular/material/dialog';

import { UrlUtils } from '../../shared/utils/url.utils';
import { Discovery } from '../../shared/configurations/discovery.constant';
import { SEOService } from '../../core/seo.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { JwtUtilService } from '../../core/jwt-util.service';
import {
    ActivatedRoute,
    QueryParamsHandling,
    Router,
    RouterLink,
} from '@angular/router';
import { ClaimOutputsService } from './claim-outputs.service';
import { ClaimAsset } from '../../shared/interfaces/claim-output-list.interface';
import { ChatDialogComponent } from '../../parts/chat/chat-dialog/chat-dialog.component';
import { SessionStorageUtilService } from '../../core/session-storage-util.service';
import { Subject, take, takeUntil, timer } from 'rxjs';
import { RouterService } from '../../core/router.service';
import { NotificationService } from '../../core/notification.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { CitationBriefComponent } from '../../parts/citations/citation-brief/citation-brief.component';
import { CdkAriaLive } from '@angular/cdk/a11y';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass, DecimalPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
    MatAnchor,
    MatButton,
    MatIconButton,
    MatMiniFabButton,
} from '@angular/material/button';
import {
    CenteredBlockLayoutComponent,
    NavMainLayoutComponent,
} from '@exl-ng/mulo-views';

@Component({
    selector: 'esp-claim-outputs',
    templateUrl: './claim-outputs.component.html',
    styleUrls: ['./claim-outputs.component.scss'],
    animations: [
        HeightInAnimation,
        HeightOutAnimation,
        WidthInAnimation,
        WidthOutAnimation,
        OpacityInAnimation,
        OpacityOutAnimation,
    ],
    imports: [
        PrologueComponent,
        CenteredBlockLayoutComponent,
        NavMainLayoutComponent,
        MatAnchor,
        RouterLink,
        MatIcon,
        MatCheckbox,
        MatButton,
        MatFormField,
        MatSelect,
        MatOption,
        StickyDirective,
        FloatingToolbarComponent,
        CdkAriaLive,
        FloatingToolbarButtonsComponent,
        FloatingToolbarSeparatorComponent,
        MatIconButton,
        LoadingSlateComponent,
        CitationBriefComponent,
        FormsModule,
        MatTooltip,
        NgClass,
        SvgViewboxDirective,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        ExpandableComponent,
        NgxPaginationModule,
        PagingComponent,
        MatDialogTitle,
        CdkScrollable,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatMiniFabButton,
        DecimalPipe,
        TranslateModule,
        HtmlSanitizePipe,
        UrlTrustPipe,
    ],
})
export class ClaimOutputsComponent implements OnInit, OnDestroy {
    media = inject(MediaService);
    private seoService = inject(SEOService);
    private translate = inject(TranslateService);
    dialog = inject(MatDialog);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private jwtUtilService = inject(JwtUtilService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private claimOutputsService = inject(ClaimOutputsService);
    private sessionStorageUtilService = inject(SessionStorageUtilService);
    private routerService = inject(RouterService);
    private notificationService = inject(NotificationService);

    readonly claimDialogTmpl = viewChild<TemplateRef<any>>('claimDialog');
    readonly rejectDialogTmpl = viewChild<TemplateRef<any>>('rejectDialog');

    readonly viewDialogTmpl = viewChild<TemplateRef<any>>('viewDialog');

    institution = UrlUtils.getParam('institution');

    paginationConfig: PaginationInstance = {
        id: 'serverPaging',
        itemsPerPage: Discovery.ASSETS_BULK_SIZE,
        currentPage: 1,
        totalItems: 0,
    };

    skipClaimDialogStorageKey = 'session_skip_claim_dialog';
    skipRejectDialogStorageKey = 'session_skip_reject_dialog';

    searchInProgress = false;
    toolbarScrolled;
    toolbarStuck = false;
    toolbarSticky = false;
    skipClaimDialog = false;
    skipRejectDialog = false;
    noResults = false;

    outputs: ClaimAsset[] = [];
    taskIdsToExtract = [];
    sortBy = 'create_date';
    pagesCount: number;

    componentDestroy = new Subject<void>();

    private readonly minuteInMilliseconds = 60000;
    private readonly timerPeriod = this.minuteInMilliseconds * 60 * 2;

    ngOnInit(): void {
        this.route.queryParams
            .pipe(takeUntil(this.componentDestroy))
            .subscribe((queryParams) => {
                this.getResearcherClaimOutputsList(queryParams);
            });

        //DESIGN DECISION - It was decided to refresh this page every 2 hours to enable changes made by the admin to reflect.
        timer(this.timerPeriod, this.timerPeriod)
            .pipe(takeUntil(this.componentDestroy))
            .subscribe(() => {
                this.getResearcherClaimOutputsList({
                    page: this.paginationConfig.currentPage,
                });
            });

        this.changeDocumentTitle();
        if (
            this.sessionStorageUtilService.getItem(
                this.skipClaimDialogStorageKey,
            ) === 'true'
        ) {
            this.skipClaimDialog = true;
        }
        if (
            this.sessionStorageUtilService.getItem(
                this.skipRejectDialogStorageKey,
            ) === 'true'
        ) {
            this.skipRejectDialog = true;
        }
    }

    changeDocumentTitle() {
        this.translate
            .get('research.outputClaim.document.title', {
                value0: this.jwtUtilService.getUserDisplayName(),
                value1: this.configurationHandlerService.mainInstitutionName(),
            })
            .subscribe((res) => {
                this.seoService.updateTitle(res);
            });
    }

    get searchOffset() {
        return (
            (this.paginationConfig.currentPage - 1) *
            this.paginationConfig.itemsPerPage
        );
    }

    get currentPageStart() {
        return this.searchOffset + 1;
    }
    get currentPageEnd() {
        return Math.min(
            this.paginationConfig.totalItems,
            this.paginationConfig.currentPage *
                this.paginationConfig.itemsPerPage,
        );
    }

    getResearcherClaimOutputsList(params) {
        this.searchInProgress = true;
        if (params.sort) {
            this.sortBy = params.sort;
        }
        const page = params.page ? params.page : 1;
        this.paginationConfig.currentPage = page;
        this.claimOutputsService
            .getResearcherClaimOutputsList(
                this.searchOffset,
                Discovery.ASSETS_BULK_SIZE,
                this.taskIdsToExtract,
                this.sortBy,
            )
            .subscribe(
                (data) => {
                    if (data) {
                        this.outputs = data.assets;
                        this.outputs.forEach(
                            (claim) => (claim.selected = false),
                        );
                        this.outputs.forEach(
                            (claim) =>
                                (claim.notification =
                                    this.getNotifications(claim)),
                        );
                        this.paginationConfig.totalItems = data.total;
                        this.pagesCount = Math.ceil(
                            this.paginationConfig.totalItems /
                                Discovery.ASSETS_BULK_SIZE,
                        );
                        this.claimOutputsService.updateNotificationCount(
                            this.paginationConfig.totalItems,
                        );
                    } else {
                        this.initNoResultsState();
                    }
                },
                () => {
                    this.initNoResultsState();
                },
                () => {
                    this.disableLoading();
                },
            );
    }

    disableLoading() {
        setTimeout(() => {
            this.searchInProgress = false;
        }, 600);
    }

    initNoResultsState() {
        this.noResults = true;
        this.outputs = [];
        this.paginationConfig.totalItems = 0;
        this.claimOutputsService.updateNotificationCount(0);
    }

    openMessagesDialog(output) {
        const dialog = this.dialog.open(ChatDialogComponent, {
            minWidth: '400px',
            panelClass: 'esp-chat-dialog',
            data: {
                title: 'research.output.list.message.window',
                model: output,
                indexElement: output.notification.index,
                chat: output.notification.notifications,
                singleInstanceChat: true,
                actions: {
                    cancel: 'research.output.list.message.window.cancel',
                    confirm: 'research.output.list.message.window.send',
                    close: true,
                },
                actionFocus: 'confirm',
            },
        });
        dialog
            .afterOpened()
            .pipe(take(1))
            .subscribe((result) => {
                this.notificationService
                    .readAllCommunicationMessage(output.mmsId)
                    .subscribe(() => {
                        output.notification.currNote = undefined;
                    });
            });
    }

    getLines(asset: ClaimAsset) {
        const lines = [];
        lines.push(asset.publicationDetails);
        lines.push(asset.conferenceDetails);
        lines.push(asset.eventDetails);
        lines.push(asset.etdDetails);
        lines.push(asset.seriesDetails);
        lines.push(asset.date);
        lines.push(asset.identifiers);
        return lines.filter((line) => line);
    }

    get pageFirstIdx() {
        return (
            (this.paginationConfig.currentPage - 1) *
                Discovery.ASSETS_BULK_SIZE +
            1
        );
    }

    get pageLastIdx() {
        return (
            (this.paginationConfig.currentPage - 1) *
                Discovery.ASSETS_BULK_SIZE +
            this.outputs?.length
        );
    }

    get pageAllSelected() {
        if (!this.outputs || this.outputs.length === 0) {
            return false;
        }
        return this.outputs?.every((output) => output.selected);
    }

    get pageSomeSelected() {
        return this.outputs?.some((output) => output.selected);
    }

    get selectedCount() {
        return this.outputs?.filter((output) => output.selected).length;
    }

    pageChange(event) {
        this.outputs?.forEach((_) => (_.selected = false));
        this.paginationConfig.currentPage = event;
        this.setRoute({ page: this.paginationConfig.currentPage }, 'merge');
        this.scrollToTop();
    }

    changeSort() {
        this.setRoute({ sort: this.sortBy, page: 1 }, 'merge');
    }

    setRoute(queryParams, queryParamsHandling: QueryParamsHandling = '') {
        this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling,
        });
    }

    scrollToTop() {
        window.scrollTo(0, 0);
    }

    handleSelectAllChange(event) {
        const toSelect = event.checked;
        this.outputs?.forEach((output) => {
            output.selected = toSelect;
        });
    }

    handleClaimOrReject(isClaim, singleIdx?: number, taskId?: string) {
        const dialogSkip = isClaim
            ? this.skipClaimDialog
            : this.skipRejectDialog;
        const dialogTmpl = isClaim
            ? this.claimDialogTmpl()
            : this.rejectDialogTmpl();

        if (!dialogSkip) {
            const dialogRef = this.dialog.open(dialogTmpl, {
                data: {
                    selectedCount: singleIdx != null ? 1 : this.selectedCount,
                    taskId,
                },
            });

            dialogRef
                .afterClosed()
                .pipe(take(1))
                .subscribe((continueProcess) => {
                    if (continueProcess) {
                        if (isClaim) {
                            this.claim(taskId);
                            if (this.skipClaimDialog) {
                                this.sessionStorageUtilService.setItem(
                                    this.skipClaimDialogStorageKey,
                                    'true',
                                );
                            }
                        } else {
                            this.reject(taskId);
                            if (this.skipRejectDialog) {
                                this.sessionStorageUtilService.setItem(
                                    this.skipRejectDialogStorageKey,
                                    'true',
                                );
                            }
                        }
                    } else {
                        // restore the skip checkbox if cancelled
                        if (isClaim) {
                            this.skipClaimDialog = dialogSkip;
                        } else {
                            this.skipRejectDialog = dialogSkip;
                        }
                    }
                });
        } else {
            if (isClaim) {
                this.claim(taskId);
            } else {
                this.reject(taskId);
            }
        }
    }

    view(output) {
        this.dialog.open(this.viewDialogTmpl(), {
            width: '50%',
            panelClass: 'esp-view-dialog',
            data: {
                title: output.esploroRecord?.title,
                permalink: output.permalink,
            },
        });
    }

    claim(taskId: string) {
        const taskIds = this.getTaskIdsForAction(taskId);
        this.taskIdsToExtract = this.taskIdsToExtract.concat(taskIds);
        this.claimOutputsService.claim(taskIds).subscribe((data) => {
            this.claimOutputsService.updateData();
        });
        this.refreshListAfterAction(taskIds);
    }

    reject(taskId: string) {
        const taskIds = this.getTaskIdsForAction(taskId);
        this.taskIdsToExtract = this.taskIdsToExtract.concat(taskIds);
        this.claimOutputsService.reject(taskIds).subscribe((data) => {
            this.claimOutputsService.updateData();
        });
        this.refreshListAfterAction(taskIds);
    }

    refreshListAfterAction(taskIds: string[]) {
        const page = this.getPageForListRefresh(taskIds);
        if (page !== this.paginationConfig.currentPage.toString()) {
            this.setRoute(
                //this will invoke refreshing the list again
                { page },
                'merge',
            );
        } else {
            this.getResearcherClaimOutputsList({ page });
        }
    }

    //if we remove all the outputs in the current page get back to page number 1
    getPageForListRefresh(taskIds): string {
        let page = this.paginationConfig.currentPage.toString();
        if (taskIds.length === this.outputs.length) {
            page = '1';
        }
        return page;
    }

    getTaskIdsForAction(taskId) {
        let taskIds = [];
        if (taskId) {
            taskIds.push(taskId);
        } else {
            taskIds = taskIds.concat(
                this.outputs
                    ?.filter((output) => output.selected)
                    .map((output) => output.taskId),
            );
        }
        return taskIds;
    }

    backToProfile() {
        this.routerService.goToProfile();
    }

    getNotifications(output) {
        return this.notificationService.setNoteData(output.notes, '');
    }

    ngOnDestroy() {
        this.componentDestroy.next();
        this.componentDestroy.complete();
    }
}
